/* Shared loader for the market's vehicle list — /collections/all?view=vehicle-data
   (templates/collection.vehicle-data.liquid).
   Used by the home page vehicle selector (customddAndSearch-snippet-upgrated), acv, trade-up,
   refurbished-banner and pc-exper-dd. Loads nothing by itself. Fetched once per page view,
   however many of them are on the page.

   PCVehicleData.loadProducts() → Promise<[{ handle, make, makeHandle, model, rating?, raters? }]>
     flat list, sorted by make then model.
   PCVehicleData.load() → Promise<[{ make, makeHandle, models: [{ model, handle }] }]>
     the same list grouped by make.
   Names come as entered in the admin (collection custom.make / product custom.model).

   Loading state while the list is on its way:
     PCVehicleData.loadingItem(tag = 'li') → spinner row for a list (ul / div)
     PCVehicleData.setSelectLoading(select, isLoading) → spinner inside a <select>, disabled meanwhile
     Spinner colour: --pc-vdl-color on any parent (default: the theme's foreground colour). */
window.PCVehicleData = window.PCVehicleData || (function () {
  'use strict';

  const ROUTES_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  let request = null;
  let grouped = null;

  function loadProducts() {
    if (!request) {
      request = fetch(`${ROUTES_ROOT}collections/all?view=vehicle-data`, { credentials: 'same-origin' })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) => {
          if (window.Shopify && window.Shopify.designMode) {
            if (data.missingMakes) console.warn('[pc-vd] Collections skipped, empty custom.make:', data.missingMakes);
            if (data.missingModels) console.warn('[pc-vd] Products skipped, empty custom.model:', data.missingModels);
          }
          const list = (Array.isArray(data.products) ? data.products : []).filter((p) => p && p.handle && p.make && p.model);
          /* numeric-aware: 2 Series < 10 Series, F150 < F250 */
          return list.sort((a, b) => collator.compare(a.make, b.make) || collator.compare(a.model, b.model));
        })
        .catch((err) => {
          request = null; /* allow a retry */
          grouped = null;
          throw err;
        });
    }
    return request;
  }

  function load() {
    if (!grouped) {
      /* list is already sorted, so makes and models keep that order */
      grouped = loadProducts().then((products) => {
        const makes = new Map();
        products.forEach((p) => {
          if (!makes.has(p.make)) makes.set(p.make, { make: p.make, makeHandle: p.makeHandle, models: [] });
          makes.get(p.make).models.push({ model: p.model, handle: p.handle });
        });
        return [...makes.values()];
      });
    }
    return grouped;
  }

  function productUrl(handle) {
    return `${ROUTES_ROOT}products/${encodeURIComponent(handle)}`;
  }

  /* ── Loading state ─────────────────────────────────────────
     Same spinner as the theme (base.css .spinner / .path), styles injected once. */
  const SPINNER_SVG =
    '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
    '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';
  /* <select> can't hold markup: an animated SVG as its background instead */
  const SELECT_SPINNER = `url("data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 66"><circle fill="none" stroke="#999" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30" stroke-dasharray="140 300">' +
    '<animateTransform attributeName="transform" type="rotate" from="0 33 33" to="360 33 33" dur="0.9s" repeatCount="indefinite"/></circle></svg>'
  )}")`;

  function injectStyles() {
    if (document.getElementById('pc-vdl-styles')) return;
    const style = document.createElement('style');
    style.id = 'pc-vdl-styles';
    style.textContent =
      /* !important: the host lists style their rows (li) with ID selectors */
      '.pc-vdl{display:flex!important;align-items:center!important;justify-content:center!important;width:100%!important;box-sizing:border-box;min-height:4rem;padding:1rem!important;margin:0!important;list-style:none;pointer-events:none;background:none!important;border:0!important}' +
      '.pc-vdl .spinner{width:2.4rem!important;height:2.4rem!important;margin:0!important}' +
      '.pc-vdl .path{stroke:var(--pc-vdl-color,rgb(var(--color-foreground,0,0,0)))}' +
      `.pc-vdl-select{background-image:${SELECT_SPINNER}!important;background-repeat:no-repeat!important;background-position:right .75rem center!important;background-size:1.25rem!important;cursor:progress!important}`;
    document.head.appendChild(style);
  }

  function loadingItem(tag = 'li') {
    injectStyles();
    const el = document.createElement(tag);
    el.className = 'pc-vdl';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-label', 'Loading');
    el.innerHTML = SPINNER_SVG;
    return el;
  }

  function setSelectLoading(select, isLoading) {
    if (!select) return;
    injectStyles();
    select.classList.toggle('pc-vdl-select', isLoading);
    select.setAttribute('aria-busy', String(isLoading));
    if (isLoading) select.disabled = true;
  }

  return { load, loadProducts, productUrl, collator, loadingItem, setSelectLoading };
})();
